import React, { useState } from "react";
import { EmptyState } from '../ui';
import { IconClipboard } from '../ui/Icons';
import './DataTable.css';

interface Column { key: string; label: React.ReactNode; sortable?: boolean; render?: (value: any, row: any) => React.ReactNode; align?: "left" | "center" | "right"; mono?: boolean; width?: number; }
interface DataTableProps { columns: Column[]; data: any[]; onRowClick?: (row: any) => void; emptyIcon?: React.ReactNode; emptyMessage?: string; emptyDescription?: string; emptyAction?: React.ReactNode; }

const DataTable: React.FC<DataTableProps> = ({ columns, data, onRowClick, emptyIcon, emptyMessage = "No data available", emptyDescription, emptyAction }) => {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const handleSort = (key: string) => {
    if (sortKey === key) { setSortDir(sortDir === "asc" ? "desc" : "asc"); }
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sorted = sortKey ? [...data].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av == null) return 1; if (bv == null) return -1;
    const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === "asc" ? cmp : -cmp;
  }) : data;

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>, row: any) => {
    if (!onRowClick) return;
    const target = e.target as HTMLElement;
    // Prevent row navigation when clicking interactive controls inside the row.
    if (target.closest('button, input, select, textarea, a, label')) return;
    onRowClick(row);
  };

  if (data.length === 0) {
    return <EmptyState icon={emptyIcon || <IconClipboard size={36} strokeWidth={1.2} />} title={emptyMessage} description={emptyDescription} action={emptyAction} />;
  }

  return (
    <div className="offers-data-table-wrap">
      <table className={`offers-data-table ${columns.length > 3 ? 'offers-data-table-wide' : ''}`}>
        <thead>
          <tr>{columns.map((col) => (
            <th
              key={col.key}
              className={`offers-data-table-th align-${col.align || "left"} ${col.sortable ? "sortable" : ""}`}
              onClick={() => col.sortable && handleSort(col.key)}
            >
              {col.label}
              {col.sortable && sortKey === col.key && (<span className="offers-data-table-sort-arrow">{sortDir === "asc" ? "\u25B2" : "\u25BC"}</span>)}
            </th>
          ))}</tr>
        </thead>
        <tbody>{sorted.map((row, i) => (
          <tr
            key={i}
            onClick={(e) => handleRowClick(e, row)}
            onMouseEnter={() => setHoveredRow(i)}
            onMouseLeave={() => setHoveredRow(null)}
            className={`offers-data-table-row ${hoveredRow === i ? "is-hovered" : ""} ${onRowClick ? "is-clickable" : ""}`}
          >
            {columns.map((col) => (
              <td key={col.key} className={`offers-data-table-td align-${col.align || "left"} ${col.mono ? "mono" : ""}`}>
                {col.render ? col.render(row[col.key], row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
};

export default DataTable;
